import React, { useCallback, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { EventListHeaderFormProps, EventListHeaderValidation } from "@src/validation/EventListValidation";
import styles from "@src/components/filtersPanel/FilterLayout.module.scss";
import { Button, ButtonVariant } from "@src/components/button/Button";
import { EventFilters } from "./EventFilters";
import { setSearchFilter, setPageFilter } from "@src/store/events/eventFilterSlice";
import { MixpanelCategory, mixpanelTrack } from "@src/components/utils/mixPanelUtils";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import { PAGINATION_CONFIG } from "@src/pages/home/children/eventlist/EventList.constants";
import { fetchCategories } from "@src/store/events/eventCategoriesSlice";
import { fetchArtists } from "@src/store/events/eventArtistsSlice";
import { fetchEventStates } from "@src/store/events/eventStatesSlice";
import { fetchEventStatuses } from "@src/store/events/eventStatusSlice";

export const EventListHeader: React.FC = () => {
	const [selectedFilterIndex, setSelectedFilterIndex] = useState<number | null>(null);
	const userEmail = useAppSelector((state) => state.settings.email);
	const emailKey = userEmail.toLowerCase();
	const storageKey = `eventList_saved_filters_${encodeURIComponent(emailKey)}`;
	const [params, setParams] = useSearchParams();
	const dispatch = useAppDispatch();

	useEffect(() => {
		dispatch(fetchCategories({}));
		dispatch(fetchArtists({}));
		dispatch(fetchEventStates({}));
		dispatch(fetchEventStatuses({}));
	}, [dispatch]);

	const getArrayParam = (params: URLSearchParams, key: string): Array<number> | Array<null> => {
		const val = params.get(key);
		if (!val || val === "," || val === "0,0" || val === "null,null") {
			return [null, null];
		} else {
			const data = val.split(",").map((n) => {
				const num = Number(n);
				return isNaN(num) ? null : num;
			});
			return data as Array<number> | Array<null>;
		}
	};

	const setFormValues = useCallback(
		(params: URLSearchParams) => ({
			smartSearch: params.get("smartSearch") ?? "",
			eventName: params.get("eventName") ?? "",
			artistName: params.get("artistName") ?? "",
			state: params.get("state") ?? "",
			city: params.get("city") ?? "",
			venueName: params.get("venueName") ?? "",
			presaleStartDateTime: params.get("presaleStartDateTime") ? new Date(params.get("presaleStartDateTime") as string) : null,
			presaleEndDateTime: params.get("presaleEndDateTime") ? new Date(params.get("presaleEndDateTime") as string) : null,
			presaleStartTime: params.get("presaleStartTime") ? new Date(params.get("presaleStartTime") as string) : null,
			presaleEndTime: params.get("presaleEndTime") ? new Date(params.get("presaleEndTime") as string) : null,
			eventCategory: params.get("eventCategory") ?? "",
			eventStartDate: params.get("eventStartDate") ? new Date(params.get("eventStartDate") as string) : null,
			eventEndDate: params.get("eventEndDate") ? new Date(params.get("eventEndDate") as string) : null,
			eventStatus: params.get("eventStatus") ?? "",
			capacityRange: getArrayParam(params, "capacityRange"),
			aiRange: getArrayParam(params, "aiRange"),
		}),
		[],
	);

	const [savedFilters, setSavedFilters] = useState<Array<EventListHeaderFormProps> | null>(() => {
		return JSON.parse(localStorage.getItem(storageKey) ?? "null");
	});

	const { control, handleSubmit, reset, setValue, getValues } = useForm<EventListHeaderFormProps>({
		mode: "onChange",
		resolver: yupResolver(EventListHeaderValidation),
		defaultValues: setFormValues(params),
	});

	useEffect(() => {
		reset(setFormValues(params));
	}, [params, reset, setFormValues]);

	const onSubmit: SubmitHandler<EventListHeaderFormProps> = (values) => {
		const filterData = Object.entries(values);
		filterData.forEach(([k, v]) => {
			if (Array.isArray(v) && v.length === 2 && (v[0] === null || v[0] === 0) && (v[1] === null || v[1] === 0)) {
				params.delete(k);
			} else if (v) {
				params.set(k, String(v));
			} else {
				params.delete(k);
			}
		});

		dispatch(setPageFilter({ pageNo: 1 }));

		const filteredValues = Object.fromEntries(Object.entries(values).map(([k, v]) => [k, v === "" ? null : v]));
		dispatch(setSearchFilter(filteredValues));
		setParams(params);
	};

	const FILTER_VALUES = {
		sortBy: null,
		sortDirection: null,
		pageNo: 1,
		pageSize: PAGINATION_CONFIG.PAGE_RECORDS,
		smartSearch: null,
		eventName: null,
		artistName: null,
		venueName: null,
		state: null,
		city: null,
		presaleStartDateTime: null,
		presaleEndDateTime: null,
		presaleStartTime: null,
		presaleEndTime: null,
		eventCategory: null,
		eventStartDate: null,
		eventEndDate: null,
		eventStatus: null,
		capacityRange: [null, null],
		aiRange: [null, null],
	};

	const resetFunction = () => {
		setSelectedFilterIndex(null);
		setParams({});
		reset(setFormValues(new URLSearchParams()));
		dispatch(setPageFilter({ pageNo: 1 }));
		dispatch(setSearchFilter(FILTER_VALUES));
	};

	useEffect(() => {
		const savedFilterLS = JSON.parse(localStorage.getItem(storageKey) ?? "null");
		setSavedFilters(savedFilterLS);
	}, [userEmail]);

	useEffect(() => {
		setSelectedFilterIndex(null);
		setParams({});
		reset(setFormValues(new URLSearchParams()));
	}, []);

	const onFiltersSave = () => {
		const values = getValues();
		// Check if filter is blank
		const hasAnyFilter = Object.values(values).some((v) => {
			if (Array.isArray(v)) return v.some((x) => x !== null && x !== undefined && x !== 0);
			return v !== null && v !== undefined && v !== "";
		});
		if (!hasAnyFilter) {
			// Do not save blank filter
			return;
		}
		const currentFilters = JSON.parse(localStorage.getItem(storageKey) ?? "null") || [];
		let combinedFilters;
		if (selectedFilterIndex !== null && selectedFilterIndex >= 0 && selectedFilterIndex < currentFilters.length) {
			// Update existing filter
			combinedFilters = [...currentFilters];
			combinedFilters[selectedFilterIndex] = values;
		} else {
			// Add new filter
			combinedFilters = [...currentFilters, values];
		}
		setSavedFilters(combinedFilters);
		localStorage.setItem(storageKey, JSON.stringify(combinedFilters));
		mixpanelTrack(MixpanelCategory.SAVED_FILTERS);
	};

	const eventFilterProps = {
		control,
		reset,
		setValue,
		savedFilters: savedFilters || [],
		selectedFilterIndex,
		setSelectedFilterIndex,
		onSaveFilters: onFiltersSave,
	};

	return (
		<form className={styles.panelForm} onSubmit={handleSubmit(onSubmit)}>
			<div className={styles.panelBody}>
				<EventFilters {...eventFilterProps} />
			</div>
			<div className={styles.panelFooter}>
				<Button type="button" variant={ButtonVariant.Secondary} onClick={resetFunction}>
					Reset filters
				</Button>
				<Button type="submit">Search</Button>
			</div>
		</form>
	);
};
